import { forwardRef } from 'react';
import type { PropsWithoutRef } from 'react';

import type { NoChildrenProp, OmitStrict, RefElement, WithAttributes } from '../../types';

import type { FileDisplayProps } from './FileDisplay';
import FileList, { type FileListProps } from './FileList';
import CompactList, { type CompactListProps } from './CompactList';

export type FileDisplayListProps = WithAttributes<
  'div',
  Pick<FileDisplayProps, 'variant' | 'compact'> &
    NoChildrenProp &
    Partial<
      Pick<CompactListProps, 'count' | 'onDialogOpen' | 'onDialogClose' | 'heading' | 'progress'>
    > &
    Pick<FileListProps, 'onDownloadAll'> & {
      items?: OmitStrict<FileDisplayProps, 'variant'>[];
    }
>;

const FileDisplayList = forwardRef<
  RefElement<FileDisplayListProps>,
  PropsWithoutRef<FileDisplayListProps>
>(function FileDisplayList(
  {
    variant,
    items = [],
    compact = false,
    heading,
    progress,
    count,
    onDialogOpen,
    onDialogClose,
    onDownloadAll,
    ...restProps
  },
  ref
) {
  const fileListProps: FileListProps = {
    type: 'display',
    items: items.map(item => ({ ...item, variant })),
    onDownloadAll
  };

  return compact ? (
    <CompactList
      {...restProps}
      count={count ?? items.length}
      onDialogOpen={onDialogOpen}
      onDialogClose={onDialogClose}
      heading={heading}
      progress={progress}
      content={progress ? undefined : <FileList {...fileListProps} />}
      ref={ref}
    />
  ) : (
    <FileList {...restProps} {...fileListProps} ref={ref} />
  );
});

export default FileDisplayList;
